import {
  ARRAY_VALUE_FIELD_TYPES, FieldBlock, FieldOption, FieldType, FormBlock, FormDefinition, GroupBlock, ListBlock,
  OPTION_FIELD_TYPES
} from '../models/form-schema.model';
import {ReviewType, Submission, SubmissionFieldValue, SubmissionStatus} from '../models/submission.model';

/**
 * Pure presentation helpers for form submissions (status chips + field labels). Shared so the employee self-view
 * and any manager viewer render consistently; unit-tested.
 */

/** German label for a submission status. */
export function submissionStatusLabel(status: SubmissionStatus): string {
  switch (status) {
    case SubmissionStatus.Draft: return 'Entwurf';
    case SubmissionStatus.Submitted: return 'Eingereicht';
    case SubmissionStatus.Approved: return 'Genehmigt';
    case SubmissionStatus.Rejected: return 'Abgelehnt';
    case SubmissionStatus.Void: return 'Ungültig';
    case SubmissionStatus.Archived: return 'Archiviert';
    default: return '–';
  }
}

/** Tailwind chip classes for a submission status (matches the style guide's status-badge palette). */
export function submissionStatusClass(status: SubmissionStatus): string {
  switch (status) {
    case SubmissionStatus.Approved: return 'bg-green-50 text-green-700';
    case SubmissionStatus.Rejected:
    case SubmissionStatus.Void: return 'bg-red-50 text-red-700';
    case SubmissionStatus.Draft: return 'bg-gray-100 text-gray-600';
    default: return 'bg-theme-primary-50 text-theme-primary-700';
  }
}

/** All review types a form can opt into, in display order. Extend as new review kinds are added. */
export const ALL_REVIEW_TYPES: ReviewType[] = [ReviewType.Payroll];

/** German label for a review type. */
export function reviewTypeLabel(type: ReviewType): string {
  switch (type) {
    case ReviewType.Payroll: return 'Lohnbuchhaltung';
    default: return ReviewType[type] ?? '–';
  }
}

/** Short German explanation of what requiring a review type means for a form. */
export function reviewTypeDescription(type: ReviewType): string {
  switch (type) {
    case ReviewType.Payroll:
      return 'Einreichungen erscheinen im Zeiten-Export, bis ein Lohnbuchhalter sie als erledigt markiert.';
    default: return '';
  }
}

/** Whether a submission has been handled for a given review type (a review marker of that type exists). */
export function isReviewHandled(submission: Submission, type: ReviewType): boolean {
  return !!submission.reviews?.some(r => r.type === type);
}

/** German chip label for a payroll-review state. */
export function payrollReviewLabel(handled: boolean): string {
  return handled ? 'Lohn: erledigt' : 'Lohn: ungeprüft';
}

/** Tailwind chip classes for a payroll-review state (green when handled, amber when still open). */
export function payrollReviewClass(handled: boolean): string {
  return handled ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700';
}

/**
 * A base64 `data:` URI holding an image — how a drawn `signature` field stores its value.
 *
 * Deliberately narrow: it must match what {@link FieldRendererComponent} produces (`canvas.toDataURL()`), and it
 * must **not** match an arbitrary string an employee typed. Anything else is rendered as plain text.
 */
const DATA_IMAGE = /^data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=\s]+$/i;

/**
 * Whether a stored value is an inline image (a drawn signature) and should be rendered as one.
 *
 * Before #470 both read-back views printed the raw value, so a signature came out as a page-long base64 blob
 * under its label. Guarding on the shape rather than on the field type keeps a malformed or hand-typed value
 * out of an `<img src>`.
 */
export function isDataImage(value: unknown): value is string {
  return typeof value === 'string' && DATA_IMAGE.test(value);
}

/**
 * Whether a submission field carries anything worth showing.
 *
 * Since #470 a field whose `visibleWhen` was false at submit time is **omitted** from the submission entirely
 * rather than stored as an empty string, so "no value" now means "this question did not apply" far more often
 * than it means "left blank". Printing a "–" row for each of them buried the real answers, so the read view
 * skips them. The trade-off the product owner accepted: a visible field the employee left blank and a field
 * that never applied are indistinguishable here — both simply do not appear.
 *
 * `false` and `0` are values; `null`, `undefined`, `''` and an empty array are not.
 */
export function hasReadableValue(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return false;
  return !(Array.isArray(value) && value.length === 0);
}

/**
 * Builds a `fieldKey → human label` map by walking the whole definition (groups/lists nested). Field keys are
 * globally unique, so a single flat map covers every leaf field. Falls back to the key when a field has no label.
 */
export function buildFieldLabelMap(definition: FormDefinition | null | undefined): Record<string, string> {
  const map: Record<string, string> = {};
  if (!definition) return map;

  const walk = (blocks: FormBlock[]) => {
    for (const block of blocks) {
      if (block.type === 'static-text') continue;
      const key = (block as { key?: string }).key;
      if (block.type === 'group') walk((block as GroupBlock).blocks);
      else if (block.type === 'list') walk((block as ListBlock).blocks);
      else if (key) map[key] = block.label || key;
    }
  };

  definition.pages.forEach(page => walk(page.blocks));
  return map;
}

/**
 * What a read-back view needs to know about a field in order to render its **value** (as opposed to its label).
 *
 * Deliberately only the two facts the formatter uses. Handing {@link formatFieldValue} the whole
 * {@link FieldBlock} would let it reach for validators, masks or conditions and quietly grow a dependency on the
 * builder schema; keeping it to `type` + `options` is what lets the same rule table be mirrored server-side in
 * C# (the PDF) from a much smaller presentation model.
 */
export interface FieldMeta {
  /** The field's declared type — decides date/time formatting and whether the value is an array. */
  type: FieldType;
  /** The option catalog for `select` / `multiselect` / `radio` / `checkbox-group`; absent for every other type. */
  options?: FieldOption[];
}

/**
 * Builds a `fieldKey → {type, options}` map by walking the whole definition (groups/lists nested).
 *
 * The counterpart of {@link buildFieldLabelMap} — and deliberately the **same walk**, block for block, so the two
 * cannot drift: a field that resolves a label must also resolve its type, or a read-back view would print the
 * right caption over a wrongly-formatted value. Field keys are globally unique, so a single flat map covers every
 * leaf field at any depth.
 */
export function buildFieldMetaMap(definition: FormDefinition | null | undefined): Record<string, FieldMeta> {
  const map: Record<string, FieldMeta> = {};
  if (!definition) return map;

  const walk = (blocks: FormBlock[]) => {
    for (const block of blocks) {
      if (block.type === 'static-text') continue;
      const key = (block as { key?: string }).key;
      if (block.type === 'group') walk((block as GroupBlock).blocks);
      else if (block.type === 'list') walk((block as ListBlock).blocks);
      else if (key) {
        const field = block as FieldBlock;
        map[key] = field.options ? {type: field.type, options: field.options} : {type: field.type};
      }
    }
  };

  definition.pages.forEach(page => walk(page.blocks));
  return map;
}

/**
 * A stored answer turned into something a human reads (#501).
 *
 * A discriminated union rather than a plain string because a multi-select is genuinely **several** answers: the
 * old `String(value)` / `JSON.stringify(value)` printed `["keines"]`, brackets, quotes and all. The `list` arm
 * lets each view render the items in its own idiom (a `<ul>` on screen, bullet paragraphs in the PDF) without the
 * formatter having to know any markup.
 */
export type FormattedFieldValue =
  | { kind: 'text'; text: string }
  | { kind: 'list'; items: string[] };

/** What a field with no readable answer renders as. Matches the placeholder both detail views already used. */
export const VALUE_PLACEHOLDER = '–';

/**
 * The Tailwind classes a `{kind: 'list'}` answer's `<ul>` carries in **both** read-back detail views (#501).
 *
 * Shared as a constant for the same reason {@link formatFieldValue} is: the two views are the same screen for
 * two audiences, and every time one of them was fixed alone the other kept the defect. Here the divergence was
 * not even needed to produce one — the employee view's value column is `text-right`, and `list-inside` makes the
 * bullet part of the inline flow, so right-aligning put **every marker at a different x**: "Ich bin
 * eingeschriebene:r Student:in" and "Ich beziehe eine Rente" placed their bullets ~93 px apart at 1280 px, and a
 * form with a dozen such rows read as one crooked card.
 *
 * Each class earns its place:
 * - `inline-block` + `max-w-full` — the list is a shrink-to-fit BLOCK. In the right-aligned column the inherited
 *   `text-right` then pushes the whole box to the column's right edge (so the list stays with the rest of the
 *   values instead of floating off to the left), while in the left-aligned management column it simply sits at
 *   the start. Either way the box, not each line, is what gets aligned.
 * - `text-left` — inside the box the items are left-aligned, which is what actually lines the markers up. It
 *   deliberately overrides the inherited `text-right`; that inheritance was the bug.
 * - `list-outside` + `pl-5` — the marker hangs in its own gutter, so a wrapped option continues under its own
 *   first line rather than under its bullet. `pl-5` reserves that gutter (Tailwind 1.25 rem); it is NOT a
 *   hard-coded nudge, and it must not be written `ps-5` — that name is Bootstrap's `padding-left:3rem!important`
 *   (see the style guide's Bootstrap-collision table).
 *
 * Deliberately absent: `align-top`. An `inline-block` does sit on its line box's baseline, but its own baseline
 * is its last line's, so the descender is already inside the box — measured across every story at 390 px and
 * 1280 px, the column is exactly as tall with `vertical-align: baseline` as with `top`. The class would be a
 * no-op carrying a plausible-sounding reason, which is how dead styling accumulates.
 *
 * Colour is NOT here: it depends on whether the value is masked, so the views bind `text-gray-900` /
 * `text-gray-500` on top of this list.
 */
export const LIST_VALUE_CLASSES =
  'inline-block max-w-full list-disc list-outside space-y-0.5 pl-5 text-left text-sm font-semibold break-words';

/** An ISO calendar date, optionally carrying a time part we discard (`2026-01-31`, `2026-01-31T09:00:00Z`). */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/** A wall-clock time, with optional seconds/fraction we discard (`09:30`, `09:30:00`, `09:30:00.500`). */
const ISO_TIME = /^(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

/**
 * Whether two values name the same option.
 *
 * Loose **only** across the primitive ↔ its-own-string boundary, because that is the one distinction a JSON
 * round-trip genuinely loses: an author who typed the option value `2` gets `2` back from the definition
 * document and `"2"` back from a submission stored through an HTML `<select>` (or the other way round, depending
 * on which version of the form wrote the row). The same holds for a boolean option value against a stored
 * `"true"`. Everything else is compared with `===`.
 *
 * It is **not** `==`, and that is the whole point: `==` equates `false` with `0` and with `''`, so a
 * checkbox-shaped option value would start matching an unrelated numeric one and the reader would be shown a
 * label the employee never chose. Showing the raw stored value is always better than showing a wrong label.
 *
 * The bridge is deliberately the same one the server applies (`PresentationField.OptionKey` canonicalises a
 * number or a boolean to its invariant string form), so the PDF and the screen can never resolve the same
 * stored answer to two different labels — the whole reason #501 put one formatter on each side rather than two.
 */
function sameOptionValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  return bridgesToString(a, b) || bridgesToString(b, a);
}

/** One direction of {@link sameOptionValue}'s bridge: a number/boolean against its own string form. */
function bridgesToString(value: unknown, other: unknown): boolean {
  if (typeof other !== 'string') return false;
  return (typeof value === 'number' || typeof value === 'boolean') && String(value) === other;
}

/**
 * The label of the option carrying this value, or `null` when the field has no options or none of them matches.
 *
 * `null` (rather than a fallback string) so the caller can decide: a stored value that matches no current option
 * is printed **verbatim**, never hidden. An option deleted after the submission was made is exactly that case,
 * and the answer the employee actually gave is still the legally relevant one.
 */
function optionLabel(value: unknown, options: FieldOption[] | undefined): string | null {
  const match = options?.find(option => sameOptionValue(option.value, value));
  return match ? match.label : null;
}

/** `2026-01-31` → `31.01.2026`; anything that is not a well-formed ISO date → `null` (kept verbatim upstream). */
function germanDate(value: string): string | null {
  const match = ISO_DATE.exec(value);
  if (!match) return null;
  const [, year, month, day] = match;
  // Range-check rather than round-tripping through `Date`: `new Date('2026-02-31')` is a valid Date object in
  // some engines and shifts by time zone in all of them, which is how a date can arrive a day early.
  if (+month < 1 || +month > 12 || +day < 1 || +day > 31) return null;
  return `${day}.${month}.${year}`;
}

/** `09:30:00` → `09:30`; anything that is not a well-formed wall-clock time → `null` (kept verbatim upstream). */
function germanTime(value: string): string | null {
  const match = ISO_TIME.exec(value);
  if (!match) return null;
  const [, hours, minutes] = match;
  if (+hours > 23 || +minutes > 59) return null;
  return `${hours}:${minutes}`;
}

/**
 * Formats one stored answer for a single-value (non-array) slot: the scalar rules, minus the array arm.
 *
 * Shared by the scalar path and by each entry of the list path, so a multi-select's options are looked up
 * exactly like a select's — the defect being fixed was two views formatting the same catalog two different ways.
 */
function formatScalar(value: unknown, meta: FieldMeta | null | undefined): string {
  // An option label wins over every other rule, including the boolean one: an author who wrote an option
  // `{value: true, label: 'Ja, dauerhaft'}` meant that label, not a generic "Ja".
  const label = optionLabel(value, meta?.options);
  if (label !== null) return label;

  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';

  if (typeof value === 'string') {
    // An option-based field whose value matched nothing is printed EXACTLY as stored — re-interpreting a raw
    // option value as a date would hide the stored answer, which is the one thing this formatter must never do.
    if (isOptionField(meta?.type)) return value;
    // A `date`/`time` field is formatted on its declared type; every other string is formatted on its *shape*,
    // so a submission whose definition could not be loaded still reads `31.01.2026` rather than `2026-01-31`.
    if (meta?.type === 'date') return germanDate(value) ?? value;
    if (meta?.type === 'time') return germanTime(value) ?? value;
    return germanDate(value) ?? germanTime(value) ?? value;
  }

  // Last-resort fallback, unchanged from before #501. File/signature descriptors never reach here — both views
  // branch on them first — so in practice this only catches a shape no schema produces.
  return typeof value === 'object' && value !== null ? JSON.stringify(value) : String(value);
}

/**
 * Turns a stored submission value into what the reader sees (#501).
 *
 * **This is the single place the web app decides how an answer reads.** Both detail views used to carry their own
 * near-identical `display()` doing `String(value)` / `JSON.stringify(value)`, which is why a boolean printed
 * `true`, a select printed its wire value `pauschal_2`, and a multi-select printed `["keines"]`. The same rule
 * table is mirrored server-side for the PDF, so the wording here is a contract, not a preference:
 *
 * | Value | Renders as |
 * | --- | --- |
 * | `null` / `undefined` / `''` | `–` ({@link VALUE_PLACEHOLDER}) |
 * | an option value on an option-based field | that option's `label` |
 * | an option value matching no option | the raw value, verbatim — never hidden |
 * | `true` / `false` | `Ja` / `Nein` |
 * | an array (`multiselect` / `checkbox-group`) | `{kind: 'list'}` of labels, one entry each |
 * | a `date` field, or a bare `yyyy-MM-dd` string | `dd.MM.yyyy` |
 * | a `time` field, or a bare `HH:mm[:ss]` string | `HH:mm` |
 * | anything that does not parse cleanly | itself, verbatim — never `Invalid Date` |
 *
 * `meta` is optional throughout: the definition is loaded best-effort by both views, so every rule that *can* be
 * decided from the value alone deliberately is.
 */
export function formatFieldValue(value: unknown, meta?: FieldMeta | null): FormattedFieldValue {
  if (value === null || value === undefined || value === '') return {kind: 'text', text: VALUE_PLACEHOLDER};

  if (Array.isArray(value)) {
    // `hasReadableValue` already drops an empty array upstream, but an empty `<ul>` is a worse failure than a
    // dash if one ever gets through, so the empty case answers like any other value-less field.
    if (value.length === 0) return {kind: 'text', text: VALUE_PLACEHOLDER};
    return {kind: 'list', items: value.map(entry => formatScalar(entry, meta))};
  }

  return {kind: 'text', text: formatScalar(value, meta)};
}

/** Whether this field type presents a list of options (`select` / `multiselect` / `radio` / `checkbox-group`). */
export function isOptionField(type: FieldType | undefined): boolean {
  return !!type && OPTION_FIELD_TYPES.includes(type);
}

/** Whether this field type stores its answer as an array (`multiselect` / `checkbox-group`). */
export function isArrayValueField(type: FieldType | undefined): boolean {
  return !!type && ARRAY_VALUE_FIELD_TYPES.includes(type);
}

/**
 * Builds a `fieldKey → position` map in definition order (depth-first through pages → blocks → nested
 * groups/lists). Field keys are globally unique, so this is a total order over every leaf field.
 */
export function fieldOrderMap(definition: FormDefinition | null | undefined): Record<string, number> {
  const order: Record<string, number> = {};
  if (!definition) return order;

  let position = 0;
  const walk = (blocks: FormBlock[]) => {
    for (const block of blocks) {
      if (block.type === 'static-text') continue;
      const key = (block as { key?: string }).key;
      if (block.type === 'group') walk((block as GroupBlock).blocks);
      else if (block.type === 'list') walk((block as ListBlock).blocks);
      else if (key && !(key in order)) order[key] = position++;
    }
  };

  definition.pages.forEach(page => walk(page.blocks));
  return order;
}

/**
 * A submission field value that still knows **where** it came from (#498).
 *
 * `orderedSubmissionFields` used to flatten `submission.groups` and discard `groupKey`/`index`, so the two rows
 * of a repeating list became two values with the same `fieldKey` and no way to tell them apart. That is why the
 * detail views could only `track $index`, and why no view could address a row at all. Deliberately a **widening**
 * of `SubmissionFieldValue`, not a wrapper: every existing consumer reads `value` / `fieldKey` / `isMasked` /
 * `isSensitive` and keeps working untouched.
 */
export type OrderedSubmissionField = SubmissionFieldValue & {
  /** The wire group the value was stored in — `DEFAULT_GROUP_KEY` for everything non-repeating. */
  groupKey: string;
  /** That group's 0-based row index. Always 0 outside a repeating list. */
  row: number;
};

/**
 * Flattens a submission's field values across all groups (e.g. the `default` group and the `_files` attachments
 * group) and orders them like the form **definition**, so the read view matches the edit form. Fields not in the
 * definition (e.g. dropped in a newer version) are kept and appended after the known ones, preserving their
 * original order. The sort is stable, so repeated keys (list/repeat rows) keep their relative order.
 *
 * Ordering prefers the server-stamped `value.order` — the field's position in the submission's **own version's**
 * definition — which stays correct even after the form was later reordered (a `getDefinition` fetch only returns
 * the *latest* version). It falls back to the passed `definition`'s order when `order` is absent (older payloads).
 */
export function orderedSubmissionFields(
  definition: FormDefinition | null | undefined, submission: Submission): OrderedSubmissionField[] {
  const order = fieldOrderMap(definition);
  const rank = (v: SubmissionFieldValue) =>
    v.order ?? (v.fieldKey in order ? order[v.fieldKey] : Number.MAX_SAFE_INTEGER);
  return submission.groups
    .flatMap(group => group.values.map(value => ({...value, groupKey: group.groupKey, row: group.index})))
    .map((value, index) => ({value, index}))
    .sort((a, b) => rank(a.value) - rank(b.value) || a.index - b.index)
    .map(entry => entry.value);
}

/**
 * `listKey → human label` for every `list` block in the definition, at any depth.
 *
 * The counterpart of {@link buildFieldLabelMap}, which deliberately records only leaf fields. Doubles as the
 * "is this group key a repeating list?" test, since only a list ever produces a group other than
 * `DEFAULT_GROUP_KEY`.
 */
export function buildListLabelMap(definition: FormDefinition | null | undefined): Record<string, string> {
  const map: Record<string, string> = {};
  if (!definition) return map;

  const walk = (blocks: FormBlock[]) => {
    for (const block of blocks) {
      if (block.type === 'group') walk((block as GroupBlock).blocks);
      else if (block.type === 'list') {
        const list = block as ListBlock;
        map[list.key] = list.label || list.key;
        walk(list.blocks);
      }
    }
  };

  definition.pages.forEach(page => walk(page.blocks));
  return map;
}

/** One block of the read-back view: the non-repeating answers, or one row of a repeating list. */
export interface SubmissionSection {
  /** Stable identity for `track` — never an index. */
  id: string;
  /** `"<list label> <n>"` for a repeating row; `null` for the non-repeating answers. */
  title: string | null;
  fields: OrderedSubmissionField[];
}

/**
 * A stable per-value identity: group, row and field key (#498).
 *
 * Field keys are globally unique, so within one section this is really only the field key — but the composite
 * is what makes the identity correct for *any* rendering, flat or sectioned, and it cannot silently collide the
 * way `f.fieldKey` did across the rows of a list.
 */
export function submissionFieldId(field: OrderedSubmissionField): string {
  return [field.groupKey, field.row, field.fieldKey].join('\u0000');
}

/**
 * Groups an ordered, already-filtered field list into display sections (#499).
 *
 * Before this, a submission with three side jobs rendered "Arbeitgeber" three times in a row with nothing
 * saying which entry each belonged to — a reader could not tell three entries from one oddly-rendered entry.
 *
 * **Why a contiguous span and then a re-bucket, rather than a plain group-by.** The incoming list is sorted by
 * *field* rank, so a list's values arrive field-major: `arbeitgeber` row 0, `arbeitgeber` row 1, `eintritt`
 * row 0, `eintritt` row 1. A naive run-split would produce one section per value. But `fieldOrderMap` walks a
 * list's child blocks consecutively, so all of a list's values do form one contiguous **span** — we take that
 * span, re-bucket it by row, and emit a section per row. That keeps the list where it sits among the
 * non-repeating answers (mirroring the form) while grouping by entry.
 *
 * Rows are numbered **sequentially over the rows actually present**, not by stored index: a row cleared by the
 * #479 revision rules loses every value, is filtered out upstream, and must not leave a gap in the numbering or
 * a phantom empty section.
 */
export function groupSubmissionFields(
  definition: FormDefinition | null | undefined, fields: OrderedSubmissionField[]): SubmissionSection[] {
  const listLabels = buildListLabelMap(definition);
  const sections: SubmissionSection[] = [];
  let plain: OrderedSubmissionField[] = [];

  const flushPlain = () => {
    if (plain.length === 0) return;
    sections.push({id: `default:${sections.length}`, title: null, fields: plain});
    plain = [];
  };

  let i = 0;
  while (i < fields.length) {
    const field = fields[i];
    if (!(field.groupKey in listLabels)) {
      plain.push(field);
      i++;
      continue;
    }

    const key = field.groupKey;
    let end = i;
    while (end < fields.length && fields[end].groupKey === key) end++;

    flushPlain();

    const byRow = new Map<number, OrderedSubmissionField[]>();
    for (const entry of fields.slice(i, end)) {
      const bucket = byRow.get(entry.row);
      if (bucket) bucket.push(entry);
      else byRow.set(entry.row, [entry]);
    }

    [...byRow.keys()].sort((a, b) => a - b).forEach((row, position) => {
      sections.push({id: `${key}:${row}`, title: `${listLabels[key]} ${position + 1}`, fields: byRow.get(row)!});
    });

    i = end;
  }

  flushPlain();
  return sections;
}
